<template>
  <page-view :menu="menu">
    <template #header>
      <base-header :title="$t('message.settings')" />
    </template>

    <template #content>
      <!-- content -->
      <v-row no-gutters>
        <v-col cols="12">
          <v-row align="center" no-gutters>
            <div class="icon-container mr-3">
              <v-icon color="turquisePanda" class=""
                >mdi-badge-account-horizontal</v-icon
              >
            </div>
            <span class="text-h5">{{
              $t('message.pages.integrations.title')
            }}</span>
            <span class="caption ml-3 text--secondary"
              >| {{ $t('message.pages.integrations.subtitle') }}</span
            >
          </v-row>
        </v-col>
        <v-col class="mt-10 pa-0 pr-5" cols="12">
          <v-row align="center">
            <v-col lg="2" md="3" sm="4">
              <span class="text-body-1 font-weight-bold">
                {{ $t('message.pages.integrations.title') }}
              </span>
            </v-col>
            <v-col>
              <v-divider></v-divider>
            </v-col>
          </v-row>
          <v-row v-if="sessions.length > 0 && !sessionsLoading">
            <v-col
              cols="6"
              class="my-3"
              v-for="session in sessions"
              :key="session.id"
            >
              <v-card>
                <v-row class="pa-3" justify="space-between">
                  <img
                    v-if="session.partner.logo"
                    class="my-n3 client-logo"
                    :src="session.partner.logo"
                    :alt="session.partner.name"
                  />
                  <img
                    v-else
                    src="../../../assets/pandaMark.svg"
                    class="my-n3 client-logo"
                    :alt="session.partner.name"
                  />
                  <v-col>
                    <span class="font-weight-bold">{{
                      session.partner.name
                    }}</span>
                    <ul>
                      <li>
                        <span class="text-caption text--secondary">
                          {{ $t('message.pages.integrations.read') }}
                        </span>
                      </li>
                      <li>
                        <span class="text-caption text--secondary">
                          {{ $t('message.pages.integrations.upload') }}
                        </span>
                      </li>
                    </ul>
                  </v-col>
                  <v-card-actions>
                    <v-col>
                      <v-btn
                        v-if="session.partner.website_url"
                        @click="handleClickLink(session.partner.website_url)"
                        text
                        >SITE</v-btn
                      >
                      <v-btn
                        color="red"
                        text
                        @click="openRemoveModal(session.id)"
                        >REMOVER</v-btn
                      >
                    </v-col>
                  </v-card-actions>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
          <v-row v-else-if="!sessionsLoading" class="pa-3">
            <span>{{ $t('message.pages.integrations.notFound') }}</span>
          </v-row>

          <v-row align="center" class="mt-8">
            <v-col style="width: 450px" lg="2" md="3" sm="4">
              <span class="text-body-1 font-weight-bold">{{
                $t('message.pages.integrations.oauthClient')
              }}</span>
            </v-col>
            <v-col>
              <v-divider></v-divider>
            </v-col>
          </v-row>
          <v-col v-if="loading">
            <loading-logo :value="200" />
          </v-col>
          <v-col class="mt-5 pr-5 px-0" v-else-if="userClients.length === 0">
            <v-col cols="12" class="px-0">
              <span>
                {{ $t('message.pages.integrations.clientNotFound') }}
              </span>
            </v-col>
          </v-col>

          <v-col
            v-for="client of userClients"
            :key="client.id"
            cols="12"
            class="mt-5 pr-5 px-0"
            v-else
          >
            <v-text-field
              v-model="client.name"
              dense
              :disabled="editingClient !== client.id"
              outlined
              :label="$t('message.pages.integrations.clientName')"
            />
            <v-row>
              <v-col>
                <v-text-field
                  v-model="client.website_url"
                  dense
                  :disabled="editingClient !== client.id"
                  :rules="[rules.required, rules.isUrl]"
                  outlined
                  :label="$t('message.pages.integrations.websiteUrl')"
                />
              </v-col>
              <v-col>
                <v-text-field
                  v-model="client.callback_url"
                  dense
                  :disabled="editingClient !== client.id"
                  outlined
                  :rules="[rules.required, rules.isCallbackUrl]"
                  :label="$t('message.pages.integrations.callbackUrl')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="client.client_id"
                  append-icon="mdi-content-copy"
                  type="text"
                  @click:append="copyTextToClipboard(client.client_id)"
                  @click="copyTextToClipboard(client.client_id)"
                  outlined
                  dense
                  label="Client ID"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="client.client_secret"
                  append-icon="mdi-content-copy"
                  type="text"
                  @click:append="copyTextToClipboard(client.client_secret)"
                  @click="copyTextToClipboard(client.client_secret)"
                  outlined
                  dense
                  label="Client Secret"
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="mt-1">
              <img
                v-if="client.logo"
                :src="client.logo"
                alt="Client Logo"
                :disabled="editingClient !== client.id"
                style="width: 100px"
              />
              <v-file-input
                v-model="client.inputLogo"
                dense
                :disabled="editingClient !== client.id"
                accept="image/png"
                :error-messages="
                  errors.clientImage
                    ? $t('message.pages.integrations.invalidLogoDimensions')
                    : ''
                "
                @change="onChangeLogo(client.id)"
                :label="$t('message.pages.integrations.logo')"
              ></v-file-input>
            </v-row>
            <v-row class="pr-3 pb-8">
              <v-spacer></v-spacer>
              <v-btn
                text
                class="mr-3"
                :disabled="savingClient"
                @click="handleOpenRemoveClientModal(client.id)"
              >
                {{ $t('message.pages.integrations.remove') }}
              </v-btn>
              <v-btn
                :loading="savingClient"
                v-if="editingClient !== client.id"
                @click="editingClient = client.id"
                outlined
                class="px-5"
                >EDITAR</v-btn
              >
              <v-btn
                :disabled="savingClient"
                :loading="savingClient"
                v-else
                @click="handleUpdateClient(client.id)"
                color="primary"
                class="px-5"
                >{{ $t('message.pages.integrations.save') }}</v-btn
              >
            </v-row>
            <v-divider> </v-divider>
          </v-col>
          <v-col cols="12" v-if="!loading">
            <v-row class="pa-3" justify="end">
              <v-btn
                @click="createClientDialog = true"
                outlined
                color="primary"
                >{{ $t('message.pages.integrations.newCliente') }}</v-btn
              >
            </v-row>
          </v-col>
        </v-col>
      </v-row>

      <!-- dialogs -->
      <v-dialog v-model="createClientDialog" max-width="550">
        <v-card>
          <v-card-title class="text-h5">
            <v-icon class="pr-2" large color="primary">mdi-code-tags</v-icon>
            {{ $t('message.pages.integrations.newCliente') }}
            <v-spacer></v-spacer>
            <v-btn icon @click="createClientDialog = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-card-title>

          <v-card-text>
            {{ $t('message.pages.integrations.dialogSubtitle') }}
          </v-card-text>
          <v-col cols="12" class="px-5">
            <v-form ref="form" v-model="valid" v-on:submit.prevent>
              <v-text-field
                v-model="newClient.name"
                autofocus
                dense
                outlined
                :rules="[rules.required, rules.maxLength, rules.minLength]"
                :label="$t('message.pages.integrations.clientName')"
              ></v-text-field>
              <v-text-field
                v-model="newClient.website_url"
                :rules="[rules.required, rules.isUrl]"
                dense
                outlined
                :label="$t('message.pages.integrations.websiteUrl')"
              ></v-text-field>
              <v-text-field
                v-model="newClient.callback_url"
                dense
                outlined
                :rules="[rules.required, rules.isCallbackUrl]"
                :label="$t('message.pages.integrations.callbackUrl')"
              ></v-text-field>
            </v-form>
          </v-col>
          <v-card-text>
            <span>
              {{ $t('message.pages.integrations.logoText') }}
            </span>
          </v-card-text>
          <v-col class="pt-0 mt-n2">
            <v-file-input
              v-model="newClient.inputLogo"
              dense
              accept="image/png"
              :error-messages="
                errors.newClientImage
                  ? $t('message.pages.integrations.invalidLogoDimensions')
                  : ''
              "
              @change="onChangeNewLogo"
              :label="$t('message.pages.integrations.logo')"
            ></v-file-input>
          </v-col>
          <v-col class="py-0 mb-n4 px-6">
            <v-card flat style="height: 40px">
              <span v-if="createClientError !== ''" class="red--text">{{
                createClientError
              }}</span>
            </v-card>
          </v-col>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn
              text
              :disabled="savingClient"
              @click="createClientDialog = false"
            >
              {{ $t('message.pages.live.cancel') }}
            </v-btn>
            <v-btn
              large
              color="primary"
              class="px-3"
              :disabled="savingClient || !valid"
              @click="handleCreateClient"
              :loading="savingClient"
            >
              {{ $t('message.pages.integrations.createClient') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="removeClientModal" max-width="550px">
        <v-card>
          <v-card-title class="text-h5">
            <v-icon class="pr-2" large color="primary">mdi-alert</v-icon>
            {{ $t('message.pages.integrations.removeClient') }}
            <v-spacer></v-spacer>
            <v-btn icon @click="removeClientModal = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-card-title>

          <v-card-text>
            {{ $t('message.pages.integrations.removeClientSubtitle') }}
          </v-card-text>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn text @click="removeClientModal = false">
              {{ $t('message.pages.live.cancel') }}
            </v-btn>
            <v-btn
              large
              color="primary"
              class="px-3"
              :disabled="removeClientLoading"
              @click="handleRemoveClient"
              :loading="removeClientLoading"
            >
              {{ $t('message.pages.integrations.remove') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="removeModal" max-width="550px">
        <v-card>
          <v-card-title class="text-h5">
            <v-icon class="pr-2" large color="primary">mdi-alert</v-icon>
            {{ $t('message.pages.integrations.removeSession') }}
            <v-spacer></v-spacer>
            <v-btn icon @click="removeModal = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-card-title>

          <v-card-text>
            {{ $t('message.pages.integrations.removeSessionSubtitle') }}
          </v-card-text>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn text @click="removeModal = false">
              {{ $t('message.pages.live.cancel') }}
            </v-btn>
            <v-btn
              large
              color="primary"
              class="px-3"
              :disabled="removeLoading"
              @click="handleRemoveSession"
              :loading="removeLoading"
            >
              {{ $t('message.pages.integrations.remove') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </page-view>
</template>

<script>
// external imports
import { mapMutations } from 'vuex'

// internal imports
import i18n from '@/translations/lang'

// API
import sessionsApi from '@/services/sessionsApi'
import clientApi from '@/services/clientApi'

// Components
import LoadingLogo from '@/components/LoadingLogo.vue'
import PageView from '@/layout/PageView.vue'

export default {
  name: 'Integrations',
  components: {
    PageView,
    LoadingLogo,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
  },
  data: () => ({
    sessions: [],
    removeModal: false,
    valid: false,
    sessionToRemove: null,
    createClientDialog: false,
    rules: {
      required: (v) => (v && !!v) || i18n.t('message.requiredField'),
      maxLength: (v) =>
        (v && v.length <= 50) || i18n.t('message.maximumLength'),
      minLength: (v) => (v && v.length > 2) || i18n.t('message.minimumLength'),
      isUrl: (v) =>
        (v &&
          /^(https:\/\/www\.|https:\/\/www\.|https:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/.test(
            v
          )) ||
        i18n.t('message.pages.integrations.invalidUrl'),
      isCallbackUrl: (v) =>
        (v &&
          /^(https:\/\/www\.|https:\/\/www\.|https:\/\/|https:\/\/)[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/.test(
            v
          )) ||
        i18n.t('message.pages.integrations.invalidCallbackUrl'),
    },
    newClient: {
      name: '',
      website_url: '',
      callback_url: '',
      inputLogo: null,
      logo: null,
    },
    removeLoading: false,
    removeClientModal: false,
    removeClientLoading: false,
    sessionsLoading: false,
    createClientError: '',
    editingClient: null,
    clientToRemove: null,
    errors: {
      newClientImage: false,
      clientImage: false,
    },
    userClients: [],
    savingClient: false,
    loading: false,
  }),
  computed: {
    menu() {
      return this.$store.state.user.settingsNavItems
    },
  },
  async mounted() {
    await Promise.all([this.fetchSessions(), this.fetchClient()])
  },
  watch: {
    createClientDialog: function () {
      setTimeout(() => {
        this.newClient.name = ''
        this.newClient.website_url = ''
        this.newClient.callback_url = ''
        this.newClient.inputLogo = null
        this.newClient.logo = null
        this.newClient.imageError = false
        this.createClientError = ''
        this.errors.newClientImage = false
      }, 200)
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    copyTextToClipboard(text) {
      if (!text) {
        return
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: i18n.t('message.copiedToClipboard'),
      })
      navigator.clipboard.writeText(`${text}`)
    },
    handleOpenRemoveClientModal(id) {
      this.removeClientModal = true
      this.clientToRemove = id
    },
    async fetchClient() {
      this.loading = true
      try {
        const response = await clientApi.getClient()
        this.userClients = response.data
      } catch (error) {
        // error
      }
      this.loading = false
    },
    async getImageDimensions(file) {
      return new Promise(function (resolved) {
        var i = new Image()
        i.onload = function () {
          resolved({ w: i.width, h: i.height })
        }
        i.src = file
      })
    },
    async onChangeLogo(id) {
      const client = this.userClients.find((client) => client.id === id)
      const base64_file = await this.toBase64(client.inputLogo)
      const dimensions = await this.getImageDimensions(base64_file)
      if (dimensions.w !== 150 || dimensions.h !== 150) {
        client.inputLogo = null
        this.errors.clientImage = true
        return
      }
      this.errors.clientImage = false
      this.userClients.map((item) =>
        item.id === id ? { ...item, logo: base64_file } : item
      )
    },
    async onChangeNewLogo() {
      const base64_file = await this.toBase64(this.newClient.inputLogo)
      const dimensions = await this.getImageDimensions(base64_file)
      if (dimensions.w !== 150 || dimensions.h !== 150) {
        this.errors.newClientImage = true
        this.newClient.inputLogo = null
        return
      }
      this.errors.newClientImage = false
      this.newClient.logo = base64_file
    },
    async toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },
    async handleCreateClient() {
      this.savingClient = true
      try {
        const response = await clientApi.saveClient({
          name: this.newClient.name,
          website_url: this.newClient.website_url,
          callback_url: this.newClient.callback_url,
          logo: this.newClient.logo ? this.newClient.logo : undefined,
        })
        this.newClient = {
          name: '',
          website_url: '',
          callback_url: '',
          logo: null,
        }
        this.createClientDialog = false
        this.userClients = response.data
      } catch (error) {
        this.createClientError = error.response.data.errMsg
      }
      this.savingClient = false
    },
    async handleRemoveClient() {
      this.removeClientLoading = true
      try {
        const response = await clientApi.removeClient(this.clientToRemove)
        this.userClients = response.data
        this.clientToRemove = null
      } catch (error) {
        // error
      }
      this.removeClientModal = false
      this.removeClientLoading = false
    },
    async handleUpdateClient(id) {
      this.savingClient = true
      const client = this.userClients.find((client) => client.id === id)
      try {
        const response = await clientApi.updateClient(id, {
          name: client.name,
          website_url: client.website_url,
          callback_url: client.callback_url,
          logo: client.logo ? client.logo : undefined,
        })
        this.userClient = response.data
      } catch (error) {
        // error
      }
      this.savingClient = false
      this.editingClient = null
    },
    async openRemoveModal(session) {
      this.sessionToRemove = session
      this.removeModal = true
    },
    async handleRemoveSession() {
      this.removeLoading = true
      await sessionsApi.removeSession(this.sessionToRemove)
      await this.fetchSessions()
      this.removeLoading = false
      this.sessionToRemove = null
      this.removeModal = false
    },
    async fetchSessions() {
      this.sessionsLoading = true
      try {
        const response = await sessionsApi.getSessions()
        this.sessions = response.data
      } catch (error) {
        console.error(error)
      }
      this.sessionsLoading = false
    },
    handleClickLink(url) {
      window.open(url, '_blank')
    },
  },
}
</script>

<style>
.icon-container {
  background-color: #eaedf6;
  padding: 4px;
  border-radius: 3px;
}
.client-logo {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  height: 120px;
}
</style>
